// ============================================================
// CONFIGURAÇÃO
// ============================================================
const API_URL = "/api";


// ============================================================
// REQUEST
// ============================================================

async function request(
    endpoint,
    options = {}
) {

    const token =
        localStorage.getItem(
            "auth_token"
        );

    const headers = {
        "Content-Type":
            "application/json",

        ...(options.headers || {})
    };


    // --------------------------------------------------------
    // TOKEN
    // --------------------------------------------------------

    if (token) {

        headers.Authorization =
            `Bearer ${token}`;

    }


    let response;

    try {

        response =
            await fetch(
                `${API_URL}${endpoint}`,
                {
                    ...options,
                    headers
                }
            );

    } catch (error) {

        console.error(
            "ERRO DE CONEXÃO COM API:",
            error
        );

        throw new Error(
            "Erro ao comunicar com o servidor."
        );

    }


    // --------------------------------------------------------
    // RESPOSTA
    // --------------------------------------------------------

    let data = null;

    try {

        data =
            await response.json();

    } catch {

        data = null;

    }


    // --------------------------------------------------------
    // TOKEN EXPIRADO / NÃO AUTORIZADO
    // --------------------------------------------------------

    if (
        response.status === 401 &&
        endpoint !== "/auth/login"
    ) {

        clearAuth();

        window.dispatchEvent(
            new Event(
                "auth:logout"
            )
        );

        throw new Error(
            data?.message ||
            "Sua sessão expirou."
        );

    }


    // --------------------------------------------------------
    // ERRO HTTP
    // --------------------------------------------------------

    if (!response.ok) {

        throw new Error(
            data?.message ||
            data?.error ||
            `Erro HTTP ${response.status}.`
        );

    }


    return data;

}


// ============================================================
// AUTENTICAÇÃO
// ============================================================

export async function login(
    email,
    password
) {

    const data =
        await request(
            "/auth/login",
            {
                method:
                    "POST",

                body:
                    JSON.stringify({
                        email,
                        password
                    })
            }
        );


    // --------------------------------------------------------
    // SALVAR TOKEN
    // --------------------------------------------------------

    if (data?.token) {

        localStorage.setItem(
            "auth_token",
            data.token
        );

    }


    // --------------------------------------------------------
    // SALVAR OPERADOR
    // --------------------------------------------------------

    if (data?.operator) {

        localStorage.setItem(
            "auth_operator",
            JSON.stringify(
                data.operator
            )
        );

    }


    return data;

}


// ============================================================
// LOGOUT
// ============================================================

export async function logout() {

    try {

        await request(
            "/auth/logout",
            {
                method:
                    "POST"
            }
        );

    } catch (error) {

        console.warn(
            "Erro ao realizar logout no servidor:",
            error
        );

    } finally {

        clearAuth();

    }

}


// ============================================================
// TOKEN
// ============================================================

export function getToken() {

    return localStorage.getItem(
        "auth_token"
    );

}


// ============================================================
// OPERADOR ARMAZENADO
// ============================================================

export function getStoredOperator() {

    const value =
        localStorage.getItem(
            "auth_operator"
        );

    if (!value) {

        return null;

    }


    try {

        return JSON.parse(
            value
        );

    } catch (error) {

        console.error(
            "Erro ao ler operador armazenado:",
            error
        );

        localStorage.removeItem(
            "auth_operator"
        );

        return null;

    }

}


// ============================================================
// LIMPAR AUTENTICAÇÃO
// ============================================================

export function clearAuth() {

    localStorage.removeItem(
        "auth_token"
    );

    localStorage.removeItem(
        "auth_operator"
    );

}


// ============================================================
// INSTÂNCIAS
// ============================================================

export async function getInstances() {

    return request(
        "/conversations/instances"
    );

}


// ============================================================
// CONVERSAS
// ============================================================

export async function getConversations({
    instance = "",
    state = "",
    status = "",
    mode = ""
} = {}) {

    const params =
        new URLSearchParams();


    if (instance) {

        params.append(
            "instance",
            instance
        );

    }


    if (state) {

        params.append(
            "state",
            state
        );

    }


    if (status) {

        params.append(
            "status",
            status
        );

    }


    if (mode) {

        params.append(
            "mode",
            mode
        );

    }


    const query =
        params.toString();


    const endpoint =
        query
            ? `/conversations?${query}`
            : "/conversations";


    return request(
        endpoint
    );

}


// ============================================================
// MENSAGENS
// ============================================================

export async function getMessages(
    conversationId
) {

    if (!conversationId) {

        throw new Error(
            "Conversa não informada."
        );

    }


    return request(
        `/messages/conversation/${conversationId}`
    );

}


// ============================================================
// ENVIAR MENSAGEM
// ============================================================

export async function sendMessage({
    conversationId,
    text,
    operatorId
}) {

    if (!conversationId) {

        throw new Error(
            "Conversa não informada."
        );

    }


    if (!text?.trim()) {

        throw new Error(
            "Mensagem vazia."
        );

    }


    return request(
        "/messages/send",
        {
            method:
                "POST",

            body:
                JSON.stringify({
                    conversation_id:
                        conversationId,

                    text:
                        text.trim(),

                    operator_id:
                        operatorId
                })
        }
    );

}


// ============================================================
// ASSUMIR CONVERSA
// ============================================================

export async function assignConversation(
    conversationId,
    operatorId
) {

    return request(
        `/conversations/${conversationId}/assign`,
        {
            method:
                "POST",

            body:
                JSON.stringify({
                    operator_id:
                        operatorId
                })
        }
    );

}


// ============================================================
// VOLTAR PARA BOT
// ============================================================

export async function returnToBot(
    conversationId
) {

    return request(
        `/conversations/${conversationId}/bot`,
        {
            method:
                "POST"
        }
    );

}


// ============================================================
// ENCERRAR
// ============================================================

export async function closeConversation(
    conversationId
) {

    return request(
        `/conversations/${conversationId}/close`,
        {
            method:
                "POST"
        }
    );

}

// ============================================================
// REABRIR
// ============================================================

export async function reopenConversation(
    conversationId
) {

    return request(
        `/conversations/${conversationId}/reopen`,
        {
            method:
                "POST"
        }
    );

}


// ============================================================
// MARCAR COMO LIDA
// ============================================================

export async function markConversationAsRead(
    conversationId
) {

    if (!conversationId) {

        throw new Error(
            "Conversa não informada."
        );

    }


    return request(
        `/conversations/${conversationId}/read`,
        {
            method:
                "POST"
        }
    );

}

// ============================================================
// DEPARTAMENTOS
// ============================================================

export async function getDepartments() {

    return request(
        "/conversations/departments"
    );

}

// ============================================================
// EXPORTAÇÃO
// ============================================================

export {
    API_URL
};